import {
  OfflineSessionState,
  TransactionsRegistrarStateResponse,
  parseXml,
} from 'tax-register-core';

// fakePrro (the tax_fake_prro setting): a sale the tax register would fail goes on in a fake
// offline session instead of stopping the point of sale. A register without a context gets a
// context made up from its tax config; a register whose real offline session cannot start gets a
// fake session, without the checks, in its own context. A fake session uses the register's
// credentials in force when it has some; otherwise it is numbered like a real one
// (<OfflineSessionId>.<number>.<control number>, e.g. 21358969.10.5260) with made-up credentials,
// and the recovery upload sends its documents, renumbered from the server's position. This file
// holds the document logic; TaxService runs it.

// Made-up offline session credentials in the format the server issues: an 8-digit
// OfflineSessionId and a 15-digit seed.
export function createFakePrroCredentials(
  random: () => number = Math.random,
): OfflineSessionState {
  return {
    OfflineSessionId: String(1e7 + Math.floor(random() * 9e7)),
    OfflineSeed: String(1e14 + Math.floor(random() * 9e14)),
  };
}

// The state of a register's fake context: made up from its tax config, as a closed, empty shift at
// local number 1, without offline credentials. TaxService opens the shift in a fake offline session
// right away, so the register shows an open shift like any working one. The server's state
// replaces this one when the recovery upload has sent the documents.
export interface FakePrroState extends TransactionsRegistrarStateResponse {
  fakePrro: true;
}

export function buildFakePrroState(
  cashier: { name?: string; subjectKeyId?: string } | null | undefined,
  now: Date,
): FakePrroState {
  return {
    UID: '',
    Timestamp: now.toISOString(),
    ShiftState: 0,
    ShiftId: null,
    OpenShiftFiscalNum: null,
    ZRepPresent: false,
    Testing: false,
    Name: cashier?.name || null,
    SubjectKeyId: cashier?.subjectKeyId || null,
    FirstLocalNum: 1,
    NextLocalNum: 1,
    LastFiscalNum: null,
    OfflineSupported: true,
    ChiefCashier: false,
    OfflineSessionId: null,
    OfflineSeed: null,
    OfflineNextLocalNum: '1',
    OfflineSessionDuration: null,
    OfflineSessionsMonthlyDuration: null,
    OfflineSessionRolledBack: false,
    OfflineSessionRollbackCmdUID: null,
    Closed: false,
    fakePrro: true,
  };
}

// Whether a register's state (its context snapshot, or getState) is a fake context's.
export function isFakePrro(state: unknown): boolean {
  return (state as Partial<FakePrroState> | null | undefined)?.fakePrro === true;
}

// The header values that tell a document apart, as written: its UID (lower case), its type
// (DOCTYPE, or ZREP for a Z-report) and subtype, its fiscal number and its register.
export interface DocumentIdentity {
  uid: string;
  docType: string;
  docSubType: string;
  fiscalNumber: string;
  cashRegisterNum: string;
}

export function readDocumentIdentity(xml: string): DocumentIdentity {
  const document = parseXml<{
    CHECK?: { CHECKHEAD?: Record<string, unknown> };
    ZREP?: { ZREPHEAD?: Record<string, unknown> };
  }>(xml, { keepText: true });
  const head = document?.CHECK?.CHECKHEAD || document?.ZREP?.ZREPHEAD || {};
  const text = (value: unknown) =>
    value === null || value === undefined ? '' : String(value).trim();
  return {
    uid: text(head.UID).toLowerCase(),
    docType: document?.CHECK ? text(head.DOCTYPE) || '0' : 'ZREP',
    docSubType: text(head.DOCSUBTYPE) || '0',
    fiscalNumber: text(head.ORDERTAXNUM),
    cashRegisterNum: text(head.CASHREGISTERNUM),
  };
}

// One document of a UID: an offline copy shares its original's UID with the session's
// OfflineBegin, and its type tells them apart.
export function getDocumentKey(identity: DocumentIdentity): string {
  return `${identity.uid}|${identity.docType}|${identity.docSubType}`;
}

// The body of PUT fiscal-documents/receipt (Nt.Pos.Api): the receipt is found by its uuid,
// otherwise by the fiscal number it has (the made-up one) and its register.
export interface ReceiptFiscalDocumentUpdate {
  receiptUuid?: string;
  fiscalNumber?: string;
  taxRegisterFiscalNumber: string;
  newFiscalNumber: string;
  xmlDocument: string;
}

// What a fake document's real version takes to the backend: always its outbox entry (once per
// document key), and for a check, the update of the receipt it was issued for — by the
// receipt's uuid, or the made-up number the receipt got.
export function planRealDocumentDelivery(
  xml: string,
  fake: { orderUuid?: string; fiscalNumber?: string },
  taxRegisterFiscalNumber: string,
): { uid: string; key: string; receipt?: ReceiptFiscalDocumentUpdate } {
  const identity = readDocumentIdentity(xml);
  const receipt =
    identity.docType === '0' && (fake.orderUuid || fake.fiscalNumber)
      ? {
          receiptUuid: fake.orderUuid,
          fiscalNumber: fake.fiscalNumber,
          taxRegisterFiscalNumber:
            identity.cashRegisterNum || String(taxRegisterFiscalNumber),
          newFiscalNumber: identity.fiscalNumber,
          xmlDocument: xml,
        }
      : undefined;
  return { uid: identity.uid, key: getDocumentKey(identity), receipt };
}
